import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaCalculator, FaSearch, FaInfoCircle } from 'react-icons/fa';
import { debounce } from 'lodash';
import { searchClients as searchSuppliers, getClientById } from '../services/clientService';
import { terminology } from '../config/terminology';

const GST_MULTIPLIER = 1.1;

// The schedule screen stores each price via toFixed(2) and derives the next figure from that
// stored value, so GST is applied to an already-rounded amount. Rounding at the same points
// keeps this calculator to the cent with what the schedule will actually save.
const round2 = (value) => Number(value.toFixed(2));

const formatCurrency = (value) =>
    value === null || value === undefined || Number.isNaN(value)
        ? '—'
        : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);

// AddJobSchedulePage treats these as two independent halves, and so does this screen:
//   1. the FSE price, which for an aged care supplier also derives a duration and SEEDS the
//      invoice price — the admin can still overwrite it afterwards
//   2. the invoice price ex GST, which is a plain input for every supplier and is what the
//      GST-inclusive figure is derived from
const deriveFromFsePrice = (fsePriceInput, supplier, ndisJob) => {
    const fsePrice = parseFloat(fsePriceInput);
    if (Number.isNaN(fsePrice) || fsePrice < 0) return null;

    const fsePriceWithGst = ndisJob ? fsePrice : round2(fsePrice * GST_MULTIPLIER);
    const hourlyRate = parseFloat(supplier?.hourlyRate);
    const hasHourlyRate = !!supplier?.agedCare && !Number.isNaN(hourlyRate) && hourlyRate > 0;

    if (!hasHourlyRate) {
        return { fsePrice, fsePriceWithGst, duration: null, seededInvoiceExGst: null, hasHourlyRate };
    }

    // The raw FSE price drives the duration, not the GST-inclusive one
    let duration = fsePrice / hourlyRate;
    if (supplier?.durationInIncrements) {
        duration = Math.ceil(duration * 4) / 4; // round up to 15-minute increments
    }

    return { fsePrice, fsePriceWithGst, duration, seededInvoiceExGst: round2(duration * hourlyRate), hasHourlyRate };
};

// Quantity mirrors computeInvoiceLineItem in the api: a set fee bills once, fixed hours bills
// per hour. So for fixed hours the invoice price is a RATE and the job total is hours × rate.
const deriveInvoice = (invoiceExGstInput, ndisJob, isSetFee, allottedHoursInput) => {
    const invoiceExGst = parseFloat(invoiceExGstInput);
    if (Number.isNaN(invoiceExGst) || invoiceExGst < 0) return null;

    const hours = parseFloat(allottedHoursInput);
    const quantity = isSetFee ? 1 : (!Number.isNaN(hours) && hours > 0 ? hours : null);
    const invoiceWithGst = ndisJob ? invoiceExGst : round2(invoiceExGst * GST_MULTIPLIER);

    return {
        invoiceExGst,
        invoiceWithGst,
        quantity,
        totalExGst: quantity === null ? null : round2(invoiceExGst * quantity),
        totalWithGst: quantity === null ? null : round2(invoiceWithGst * quantity),
    };
};

const QuoteCalculatorUtility = ({ onBack }) => {
    const [supplierSearch, setSupplierSearch] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierError, setSupplierError] = useState(null);
    const [lastSelectedSupplier, setLastSelectedSupplier] = useState(''); // Track last selection so picking one does not re-search for it
    const supplierRequestRef = useRef(0); // Sequence number so a slow search cannot reopen the list after a pick

    const [franchiseePrice, setFranchiseePrice] = useState('');
    const [invoiceExGstInput, setInvoiceExGstInput] = useState('');
    const [allottedHoursInput, setAllottedHoursInput] = useState('');
    const [allocationMode, setAllocationMode] = useState('fixed-fee'); // 'fixed-fee' | 'fixed-hours'
    const [ndisJob, setNdisJob] = useState(false);

    const containerRef = useRef(null);

    const debouncedSearch = useCallback(
        debounce(async (term) => {
            if (term.trim().length < 3) {
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }
            const requestId = ++supplierRequestRef.current;
            const isCurrent = () => requestId === supplierRequestRef.current;
            setIsSearching(true);
            try {
                const data = await searchSuppliers({ searchString: term });
                if (!isCurrent()) return; // superseded by a newer search or by the user picking a supplier
                setSuggestions(data.clients || []);
                setShowSuggestions(true);
            } catch (error) {
                console.error('Supplier search failed:', error);
                if (!isCurrent()) return;
                setSuggestions([]);
                setShowSuggestions(false);
            } finally {
                if (isCurrent()) setIsSearching(false);
            }
        }, 300),
        []
    );

    useEffect(() => {
        if (supplierSearch !== lastSelectedSupplier) {
            debouncedSearch(supplierSearch);
        }
        return () => debouncedSearch.cancel();
    }, [supplierSearch, debouncedSearch, lastSelectedSupplier]);

    // Close the suggestion list when clicking elsewhere
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectSupplier = async (supplier) => {
        supplierRequestRef.current++; // discard any search still in flight
        setSupplierSearch(supplier.clientName || '');
        setLastSelectedSupplier(supplier.clientName || '');
        setSuggestions([]);
        setShowSuggestions(false);
        setSupplierError(null);
        try {
            // Refetched by id because the search result is a summary and omits the
            // agedCare / hourlyRate / durationInIncrements fields the maths needs
            const full = await getClientById(supplier.id);
            setSelectedSupplier(full);
        } catch (error) {
            console.error('Failed to load supplier:', error);
            setSelectedSupplier(null);
            setSupplierError('Could not load that supplier\'s pricing settings.');
        }
    };

    const handleClear = () => {
        setSupplierSearch('');
        setSelectedSupplier(null);
        setFranchiseePrice('');
        setInvoiceExGstInput('');
        setAllottedHoursInput('');
        setAllocationMode('fixed-fee');
        setNdisJob(false);
        setSuggestions([]);
        setShowSuggestions(false);
        setSupplierError(null);
    };

    const isSetFee = allocationMode === 'fixed-fee';
    const quote = selectedSupplier ? deriveFromFsePrice(franchiseePrice, selectedSupplier, ndisJob) : null;
    const invoice = deriveInvoice(invoiceExGstInput, ndisJob, isSetFee, allottedHoursInput);
    const gstLabel = ndisJob ? 'GST Free' : 'w/ GST';
    const seededInvoiceExGst = quote?.seededInvoiceExGst ?? null;

    // Seeds the invoice price for aged care suppliers, same as the schedule screen. Deps match
    // that screen's effect, so re-seeding on a supplier/price/NDIS change also overwrites a
    // manual edit there — kept identical rather than "improved" so the two behave the same.
    useEffect(() => {
        if (seededInvoiceExGst !== null) {
            setInvoiceExGstInput(seededInvoiceExGst.toFixed(2));
        }
    }, [seededInvoiceExGst, ndisJob]);

    // The schedule screen also writes the derived duration into allotedHours
    const seededDuration = quote?.duration ?? null;
    useEffect(() => {
        if (seededDuration !== null) setAllottedHoursInput(String(seededDuration));
    }, [seededDuration]);

    return (
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-center justify-between px-4 sm:px-0">
                <h1 className="text-2xl font-semibold text-gray-900">Quote Calculator</h1>
                <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 underline">
                    ← Back to Utilities
                </button>
            </div>

            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 mb-6">
                <p className="text-gray-600 mb-6">
                    Work out the invoice figures for a quote without creating a job schedule. Uses the same
                    calculation as the Add Job Schedule screen, so the numbers here match what that screen
                    will produce for the same {terminology.client.singular.toLowerCase()} and FSE price.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div ref={containerRef} className="relative">
                        <label htmlFor="supplier-search" className="block text-sm font-medium text-gray-700">
                            {terminology.client.singular} *
                        </label>
                        <div className="relative mt-1">
                            <input
                                id="supplier-search"
                                type="text"
                                value={supplierSearch}
                                onChange={(e) => {
                                    setSupplierSearch(e.target.value);
                                    setSelectedSupplier(null);
                                    setLastSelectedSupplier(''); // Typing is a new search, even back to the name that was picked
                                }}
                                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                placeholder="Type at least 3 characters"
                                className="block w-full px-3 py-2 pr-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                                autoComplete="off"
                            />
                            <FaSearch className="absolute right-3 top-3 text-gray-400" size={12} />
                        </div>
                        {showSuggestions && suggestions.length > 0 && (
                            <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-56 overflow-auto">
                                {suggestions.map((s) => (
                                    <li key={s.id}>
                                        <button
                                            type="button"
                                            onClick={() => handleSelectSupplier(s)}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                        >
                                            {s.clientName || 'Unnamed'}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {isSearching && <p className="mt-1 text-xs text-gray-500">Searching…</p>}
                        {supplierError && <p className="mt-1 text-xs text-red-600">{supplierError}</p>}
                    </div>

                    <div>
                        <label htmlFor="franchisee-price" className="block text-sm font-medium text-gray-700">
                            Franchisee Price
                        </label>
                        <input
                            id="franchisee-price"
                            type="number"
                            step="0.01"
                            min="0"
                            value={franchiseePrice}
                            onChange={(e) => setFranchiseePrice(e.target.value)}
                            placeholder="100.00"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm
                                [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                        <p className="mt-1 text-xs text-gray-500">Same value you would enter as FSE Price on a schedule.</p>
                    </div>

                    <div>
                        <label htmlFor="invoice-ex-gst" className="block text-sm font-medium text-gray-700">
                            Invoice Price (ex. GST)
                        </label>
                        <input
                            id="invoice-ex-gst"
                            type="number"
                            step="0.01"
                            min="0"
                            value={invoiceExGstInput}
                            onChange={(e) => setInvoiceExGstInput(e.target.value)}
                            placeholder="120.00"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm
                                [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            {quote?.hasHourlyRate
                                ? 'Filled from the hourly rate — overwrite it to quote a different price.'
                                : isSetFee ? 'The whole fee, before GST.' : 'Per hour, before GST.'}
                        </p>
                    </div>

                    <div>
                        <span className="block text-sm font-medium text-gray-700">Allocation Type</span>
                        <div className="mt-2 flex gap-4">
                            {[
                                { value: 'fixed-fee', label: 'Set Fee' },
                                { value: 'fixed-hours', label: 'Fixed Hours' },
                            ].map((opt) => (
                                <label key={opt.value} className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        name="allocationMode"
                                        value={opt.value}
                                        checked={allocationMode === opt.value}
                                        onChange={(e) => setAllocationMode(e.target.value)}
                                        className="h-4 w-4 text-jims-cyan border-gray-300 focus:ring-jims-cyan"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{opt.label}</span>
                                </label>
                            ))}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                            {isSetFee ? 'Billed once.' : 'Billed per hour — the total is hours × invoice price.'}
                        </p>
                    </div>

                    {!isSetFee && (
                        <div>
                            <label htmlFor="allotted-hours" className="block text-sm font-medium text-gray-700">
                                Allotted Hours
                            </label>
                            <input
                                id="allotted-hours"
                                type="number"
                                step="0.01"
                                min="0"
                                value={allottedHoursInput}
                                onChange={(e) => setAllottedHoursInput(e.target.value)}
                                placeholder="2"
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm
                                    [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                            <p className="mt-1 text-xs text-gray-500">Hours billed on the invoice.</p>
                        </div>
                    )}

                    <div className="flex flex-col justify-start">
                        <span className="block text-sm font-medium text-gray-700">NDIS</span>
                        <label className="mt-2 inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={ndisJob}
                                onChange={(e) => setNdisJob(e.target.checked)}
                                className="h-4 w-4 text-jims-cyan border-gray-300 rounded focus:ring-jims-cyan"
                            />
                            <span className="ml-2 text-sm text-gray-700">NDIS job (GST free)</span>
                        </label>
                        <button
                            type="button"
                            onClick={handleClear}
                            className="mt-4 self-start text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </div>

            {selectedSupplier && (
                <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
                    <div className="flex items-center mb-4">
                        <FaCalculator className="text-jims-cyan mr-2" />
                        <h2 className="text-lg font-medium text-gray-900">
                            {selectedSupplier.clientName || 'Selected supplier'}
                        </h2>
                        {selectedSupplier.agedCare && (
                            <span className="ml-3 inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                Aged Care · ${Number(selectedSupplier.hourlyRate || 0).toFixed(2)}/hr
                                {selectedSupplier.durationInIncrements ? ' · 15 min increments' : ''}
                            </span>
                        )}
                    </div>

                    {!quote && !invoice ? (
                        <p className="text-sm text-gray-500">Enter a franchisee price or an invoice price to see the figures.</p>
                    ) : (
                        <>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="border border-gray-200 rounded-md p-4">
                                    <dt className="text-xs uppercase tracking-wide text-gray-500">FSE Price ({gstLabel})</dt>
                                    <dd className="mt-1 text-xl font-semibold text-gray-900">{formatCurrency(quote?.fsePriceWithGst)}</dd>
                                </div>
                                <div className="border border-gray-200 rounded-md p-4">
                                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                                        Invoice Price (ex. GST){!isSetFee && ' / hr'}
                                    </dt>
                                    <dd className="mt-1 text-xl font-semibold text-gray-900">{formatCurrency(invoice?.invoiceExGst)}</dd>
                                </div>
                                <div className="border border-gray-200 rounded-md p-4">
                                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                                        Invoice Price ({gstLabel}){!isSetFee && ' / hr'}
                                    </dt>
                                    <dd className="mt-1 text-xl font-semibold text-gray-900">{formatCurrency(invoice?.invoiceWithGst)}</dd>
                                </div>
                                {/* The schedule screen writes this same duration into allotedHours */}
                                <div className="border border-gray-200 rounded-md p-4">
                                    <dt className="text-xs uppercase tracking-wide text-gray-500">Allotted Hours</dt>
                                    <dd className="mt-1 text-xl font-semibold text-gray-900">
                                        {quote?.duration == null ? '—' : `${quote.duration.toFixed(2)} hrs`}
                                    </dd>
                                </div>
                            </dl>

                            {/* What the supplier is actually invoiced: quantity × rate, where a set
                                fee bills once and fixed hours bills per hour */}
                            <div className="mt-4 border-2 border-jims-cyan rounded-md p-4 bg-cyan-50">
                                <div className="flex flex-wrap items-baseline justify-between gap-3">
                                    <div>
                                        <p className="text-xs uppercase tracking-wide text-gray-600">
                                            Invoice total ({gstLabel})
                                        </p>
                                        <p className="mt-1 text-2xl font-bold text-gray-900">
                                            {formatCurrency(invoice?.totalWithGst)}
                                        </p>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        {invoice?.quantity == null
                                            ? 'Enter allotted hours to see the total.'
                                            : `${invoice.quantity} × ${formatCurrency(invoice.invoiceWithGst)}`}
                                        {invoice?.totalExGst != null && (
                                            <span className="block text-xs text-gray-500">
                                                {formatCurrency(invoice.totalExGst)} ex. GST
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>

                            {!quote?.hasHourlyRate && (
                                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start">
                                    <FaInfoCircle className="text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                                    <p className="text-sm text-blue-800">
                                        This {terminology.client.singular.toLowerCase()} has no aged care hourly rate, so there is
                                        no rule to derive the invoice price from the franchisee price — enter it above. The Add
                                        Job Schedule screen works the same way.
                                    </p>
                                </div>
                            )}

                            {quote?.hasHourlyRate && (
                                <p className="mt-4 text-sm text-gray-500">
                                    Duration {quote.duration.toFixed(2)} hours based on hourly rate of{' '}
                                    ${Number(selectedSupplier.hourlyRate).toFixed(2)} + GST.
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuoteCalculatorUtility;
